import { memo, useState, useEffect, useCallback, useRef, forwardRef, useImperativeHandle, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { isArray, noop, isEmpty, isNil } from 'lodash';
import { useLocation } from 'react-router-dom';
// Import Types
import type { CampaignSegmentRowData, ListHeaderConfig, SegmentListRowItem } from 'types';
import type { UseListFiltersReturnValue } from 'hooks/useListFilters';
// Import Components
import DataTable, { type DataTableRefActions } from 'components/common/DataTable';
import SegmentActionsModals from './SegmentActionsModals';
import SegmentActionsButtons from './SegmentActionsButtons';
// Import Hooks
import useGoToRoute from 'hooks/GoToRoute';
import useToaster from 'hooks/UseToaster';
import useRequest from 'hooks/UseRequest';
import useCancelableRequests from 'hooks/UseCancelableRequests';
import useQueryParams from 'hooks/UseQueryParams';
import { usePartnerDateFormats } from 'hooks/UsePartnerDateFormats';
// Import Context
import { useModalActionsContext } from 'context/modalActionsContext';
// Import Helpers
import { getPageNumber } from 'services/Helpers';
// Import Services
import { SegmentHttpService } from 'services/http';
// Import Constants
import { ModalsActions, SegmentTypesLabels, TargetTypeEnum, l } from 'constants/common';
import { dataTableColumnsConfigs } from './config';
import { RoutesList } from 'routes';
// Import Styles
import 'assets/scss/segmentListPage.scss';

const {
    SEGMENT_DYNAMIC_EDIT,
    SEGMENT_DYNAMIC_CLONE,
    SEGMENT_COMPOSITE_EDIT,
    SEGMENT_COMPOSITE_CLONE,
    CONVERT_TO_REPORT,
    SEGMENT_PREDEFINED_CLONE,
} = RoutesList;

interface Props {
    getRadioSelectedRow?: (row: unknown) => void;
    isShowVerticalDots?: boolean;
    withManageLabels?: boolean;
    isViewMode?: boolean;
    getHeaderActions?: () => void;
    rowsUpdateCallback?: (rows: unknown[]) => void;
    isHasRowMultiSelect?: boolean;
    isHasRowRadioSelect?: boolean;
    isHasBulkActions?: boolean;
    isShowColumnChooser?: boolean;
    defaultRadioSelectedRow?: unknown;
    getDragAcceptType?: (row: string) => void;
    CustomPreview?: React.ReactNode;
    dragDisable?: boolean;
    tableKey?: string;
    withQueryParams?: boolean;
    dataTableClassNames?: string;
    getTitle?: (row: AnyObject) => string;
    radioSelectColumnConfig?: unknown;
    isVisibleHeader?: boolean;
    isHasLeftHeaderActions?: boolean;
    headerLeftActions?: ReactNode;
    listFilters: UseListFiltersReturnValue;
    /** Compact single-row header; when set, `getHeaderActions` is ignored. */
    headerConfig?: ListHeaderConfig;
}

export interface SegmentListRef {
    resetSelectedRows: () => void;
    reset: () => void;
}

const SegmentList = forwardRef<SegmentListRef, Props>((props, ref) => {
    const {
        getTitle = () => '',
        isViewMode = false,
        getHeaderActions = noop,
        isShowVerticalDots = true,
        isShowColumnChooser = true,
        withManageLabels = false,
        rowsUpdateCallback = noop,
        isHasRowMultiSelect = false,
        isHasBulkActions = false,
        isHasRowRadioSelect = false,
        getRadioSelectedRow = noop,
        defaultRadioSelectedRow = null,
        getDragAcceptType = () => '',
        CustomPreview,
        dragDisable = false,
        tableKey = 'segmentListDataTable',
        withQueryParams = false,
        dataTableClassNames = 'default-height',
        radioSelectColumnConfig,
        isVisibleHeader,
        isHasLeftHeaderActions,
        headerLeftActions,
        listFilters,
        headerConfig,
    } = props;
    const { t } = useTranslation();
    const { goToPath, getRoutePath } = useGoToRoute();
    const { search } = useLocation();
    const { showToaster } = useToaster();
    const { doPostRequest, doGetRequest } = useRequest();
    const goToRouteQueryParams = useQueryParams();
    const dateFormats = usePartnerDateFormats();
    const { modalActionsRef } = useModalActionsContext();

    const dataTableRef = useRef<DataTableRefActions>(null);

    const { isMounted, baseRequestBody, changePageNumber, changePageSize, changeSorting } = listFilters;

    const [isSegmentListLoading, setIsSegmentListLoading] = useState(false);
    const [segmentListData, setSegmentListData] = useState<CampaignSegmentRowData[]>([]);
    const [segmentListDataCount, setSegmentListDataCount] = useState(0);

    const [isDeleteModalOpenedState, setIsDeleteModalOpenedState] = useState(false);
    const [isConvertToReportModalOpenedState, setIsConvertToReportModalOpenedState] = useState(false);
    const [isArchiveModalOpenedState, setIsArchiveModalOpenedState] = useState(false);
    const [isUnArchiveModalOpenedState, setIsUnArchiveModalOpenedState] = useState(false);
    const [isInfoModalOpenedState, setIsInfoModalOpenedState] = useState(false);
    const [openedModalData, setOpenedModalData] = useState<AnyObject>({});
    const [isUsedInConfirmationModalOpenedState, setIsUsedInConfirmationModalOpenedState] = useState(false);

    const { columns } = dataTableColumnsConfigs(t, dateFormats);

    // Segment fetch part
    const { getSegmentListRequest, getCampaignsBySegmentRequest } = useCancelableRequests(
        {
            getSegmentListRequest: SegmentHttpService.getSegmentList,
            getCampaignsBySegmentRequest: SegmentHttpService.getCampaignsBySegment,
        },
        { takeLatest: ['getSegmentListRequest'] },
    );

    const getSegments = useCallback(
        (filter = baseRequestBody) => {
            setIsSegmentListLoading(true);

            doPostRequest(getSegmentListRequest.request, {
                requestBody: filter,
                successCallback: ({ Data, Count }) => {
                    const rows = getTableRows(Data || []);
                    setSegmentListData(rows);
                    rowsUpdateCallback(rows);
                    setSegmentListDataCount(Count);
                },
            }).then(({ IsCanceled }) => {
                // A superseded call must not hide the loader of the call that replaced it
                if (!IsCanceled) {
                    setIsSegmentListLoading(false);
                }
            });
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [baseRequestBody],
    );

    const getEditSegmentPath = (segment) => {
        const { Type, SegmentId } = segment;
        const params = { segmentId: SegmentId };
        const path = SegmentTypesLabels.Dynamic === Type ? SEGMENT_DYNAMIC_EDIT : SEGMENT_COMPOSITE_EDIT;

        return getRoutePath(path, params, goToRouteQueryParams.encode({ backParams: search }));
    };

    const goToEditSegment = (segment) => {
        goToPath(getEditSegmentPath(segment));
    };

    const getConvertSegmentToReportPath = (segment) => {
        const { SegmentId } = segment;
        const params = { segmentId: SegmentId };
        const path = CONVERT_TO_REPORT;
        return getRoutePath(path, params, goToRouteQueryParams.encode({ backParams: search }));
    };

    // Actions part
    const deleteSegmentAction = (segment) => {
        setOpenedModalData(segment);
        setIsDeleteModalOpenedState(true);
    };

    const archiveSegmentAction = (segment) => {
        setOpenedModalData(segment);
        setIsArchiveModalOpenedState(true);
    };

    const unArchiveSegmentAction = (segment) => {
        setOpenedModalData(segment);
        setIsUnArchiveModalOpenedState(true);
    };

    const infoSegmentAction = (segment) => {
        setOpenedModalData(segment);
        setIsInfoModalOpenedState(true);
    };

    useImperativeHandle(modalActionsRef, () => ({
        onInfoModalOpen: infoSegmentAction,
    }));

    const getCloneSegmentPath = (segment) => {
        const { Type, SegmentId } = segment;
        const params = { segmentId: SegmentId };
        if (SegmentTypesLabels.Dynamic === Type) {
            return getRoutePath(SEGMENT_DYNAMIC_CLONE, params);
        } else {
            return getRoutePath(SEGMENT_COMPOSITE_CLONE, params);
        }
    };

    const editSegmentAction = (segment) => {
        doGetRequest(getCampaignsBySegmentRequest.request, {
            queryString: { segmentId: segment.SegmentId },
            successCallback: (Data) => {
                if (isEmpty(Data)) {
                    goToEditSegment(segment);
                } else {
                    setOpenedModalData({ objects: Data.map(({ Name, Type }) => ({ Name, Type })), segment });
                    setIsUsedInConfirmationModalOpenedState(true);
                }
            },
        });
    };

    const getClonePredefinedPath = ({ SegmentId }) => {
        const params = { segmentId: SegmentId };
        const route = SEGMENT_PREDEFINED_CLONE;
        return getRoutePath(route, params);
    };

    const modalsStateSetDict = {
        deleteSegment: {
            key: 'deleteSegment',
            fn: setIsDeleteModalOpenedState,
        },
        convertToReport: {
            key: 'convertToReport',
            fn: setIsConvertToReportModalOpenedState,
        },
        archiveSegment: {
            key: 'archiveSegment',
            fn: setIsArchiveModalOpenedState,
        },
        unarchiveSegment: {
            key: 'unarchiveSegment',
            fn: setIsUnArchiveModalOpenedState,
        },
        infoSegment: {
            key: 'infoSegment',
            fn: setIsInfoModalOpenedState,
        },
        editSegment: {
            key: 'editSegment',
            fn: setIsUsedInConfirmationModalOpenedState,
        },
    };

    const onCloseModalHandler = (modalStateKey, action, alertType, alertMessage, count = 1) => {
        const isConvertToReport = action === ModalsActions.CONVERT_TO_REPORT;

        if (
            action === ModalsActions.DELETE ||
            isConvertToReport ||
            action === ModalsActions.ARCHIVE ||
            action === ModalsActions.UNARCHIVE
        ) {
            if (
                !isConvertToReport ||
                (!openedModalData?.IsUsed && openedModalData?.Type === SegmentTypesLabels.Static)
            ) {
                dataTableRef.current?.resetSelectedRows?.();
                dataTableRef.current?.changePaginationPageNumber?.(
                    getPageNumber(
                        baseRequestBody.Filters,
                        action,
                        segmentListDataCount,
                        baseRequestBody.Pageing.PageSize,
                        baseRequestBody.Pageing.PageNumber,
                        count,
                    ),
                );
                getTableRefreshFn();
            }
            showToaster(alertType, alertMessage);
        }

        modalsStateSetDict[modalStateKey].fn(false);
    };

    const getRowActionBar = (row) => {
        const { data } = row;

        return (
            <>
                <SegmentActionsButtons
                    data={data}
                    isInfoVisible={true}
                    isViewMode={isViewMode}
                    getConvertSegmentToReportPath={getConvertSegmentToReportPath}
                    unArchiveSegmentAction={unArchiveSegmentAction}
                    archiveSegmentAction={archiveSegmentAction}
                    deleteSegmentAction={deleteSegmentAction}
                    getCloneSegmentPath={getCloneSegmentPath}
                    editSegmentAction={editSegmentAction}
                    getEditSegmentPath={getEditSegmentPath}
                    infoSegmentAction={infoSegmentAction}
                    getClonePredefinedPath={getClonePredefinedPath}
                />
            </>
        );
    };

    const getTableColumns = () => {
        return columns.map((item) => ({ ...item, sortable: item.sortable && dragDisable }));
    };

    const getTableRows = useCallback(
        (segmentList) => {
            return segmentList.map(
                ({
                    Name,
                    Type,
                    IsUsed,
                    CreatorName,
                    State,
                    SegmentId,
                    DefinitionType,
                    CreatedDate,
                    CompositeConfiguration,
                    CategoryId,
                    CategoryName,
                    CategoryColor,
                    TargetObject,
                    ArchivedDate,
                    VirtualCurrency,
                }): SegmentListRowItem => {
                    const isComposite = Type === SegmentTypesLabels.Composite;

                    return {
                        hasHover: true,
                        dragDisable: dragDisable,
                        isComposite: isComposite,
                        isRowExpandable: isComposite,
                        nestedData: [],
                        className: !isNil(ArchivedDate) ? 'crm-data-table-row-archive' : '',
                        data: {
                            isRowOpend: false,
                            State,
                            Name,
                            Type,
                            IsUsed,
                            CreatorName,
                            SegmentId,
                            DefinitionType,
                            CreatedDate,
                            CompositeConfiguration,
                            CategoryId,
                            CategoryName,
                            CategoryColor,
                            TargetObject,
                            ArchivedDate,
                            VirtualCurrency,
                        },
                    };
                },
            );
        },
        [dragDisable],
    );

    const getTableRefreshFn = useCallback(() => {
        getSegments(baseRequestBody);
    }, [baseRequestBody, getSegments]);

    const getDataTableName = () => <span>{t(l.Segments)}</span>;

    // Filter update part
    useEffect(() => {
        if (isMounted.current) {
            getSegments(baseRequestBody);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [baseRequestBody]);

    useImperativeHandle(ref, () => ({
        resetSelectedRows() {
            dataTableRef.current?.resetSelectedRows?.();
        },
        reset() {
            dataTableRef.current?.reset?.();
        },
    }));

    const isAllow = (segment) => {
        const { IsUsed, ArchivedDate } = segment;
        const isArchive = !isNil(ArchivedDate);

        return {
            delete: !IsUsed,
            archive: !isArchive,
            unarchive: isArchive,
        };
    };

    const getSegmentsNamesIds = (selectedRows) => {
        if (!isArray(selectedRows)) return;

        interface SegmentData {
            segmentId: number;
            name?: string;
        }

        const allowDeleteSegments: SegmentData[] = [];
        const notAllowDeleteSegments: SegmentData[] = [];
        const allowArchiveSegments: SegmentData[] = [];
        const notAllowArchiveSegments: SegmentData[] = [];
        const allowUnarchiveSegments: SegmentData[] = [];
        const notAllowUnarchiveSegments: SegmentData[] = [];

        selectedRows.forEach(({ data }) => {
            const { SegmentId, Name } = data;
            const isAllowData = isAllow(data);
            if (isAllowData.delete) {
                allowDeleteSegments.push({ segmentId: SegmentId, name: Name });
            } else {
                notAllowDeleteSegments.push({ segmentId: SegmentId, name: Name });
            }
            if (isAllowData.archive) {
                allowArchiveSegments.push({ segmentId: SegmentId, name: Name });
            } else {
                notAllowArchiveSegments.push({ segmentId: SegmentId, name: Name });
            }

            if (isAllowData.unarchive) {
                allowUnarchiveSegments.push({ segmentId: SegmentId, name: Name });
            } else {
                notAllowUnarchiveSegments.push({ segmentId: SegmentId, name: Name });
            }
        });

        return {
            allowDeleteSegments,
            notAllowDeleteSegments,
            allowArchiveSegments,
            notAllowArchiveSegments,
            allowUnarchiveSegments,
            notAllowUnarchiveSegments,
        };
    };

    const deleteSelectedRows = (selectedRows) => {
        const segmentNamesIds = getSegmentsNamesIds(selectedRows);
        const namesIds = {
            allowDeleteSegments: segmentNamesIds?.allowDeleteSegments,
            notAllowDeleteSegments: segmentNamesIds?.notAllowDeleteSegments,
        };
        deleteSegmentAction(namesIds);
    };

    const archiveSelectedRows = (selectedRows) => {
        const segmentNamesIds = getSegmentsNamesIds(selectedRows);
        const namesIds = {
            allowArchiveSegments: segmentNamesIds?.allowArchiveSegments,
            notAllowArchiveSegments: segmentNamesIds?.notAllowArchiveSegments,
        };
        archiveSegmentAction(namesIds);
    };

    const unarchiveSelectedRows = (selectedRows) => {
        const segmentNamesIds = getSegmentsNamesIds(selectedRows);
        const namesIds = {
            allowUnarchiveSegments: segmentNamesIds?.allowUnarchiveSegments,
            notAllowUnarchiveSegments: segmentNamesIds?.notAllowUnarchiveSegments,
        };
        unArchiveSegmentAction(namesIds);
    };

    return (
        <>
            <DataTable
                ref={dataTableRef}
                classNames={dataTableClassNames}
                getTitle={getTitle}
                isVisibleHeader={isVisibleHeader}
                rowKey="SegmentId"
                columnKey="dataKey"
                name={getDataTableName()}
                isColumnsSortable={true}
                tableKey={tableKey}
                withPagination={true}
                withManageLabels={withManageLabels}
                setLabelObjectIdKey="SegmentId"
                labelNameKey="CategoryName"
                labelColorKey="CategoryColor"
                labelObjectType={TargetTypeEnum.Segment}
                data={segmentListData}
                dataCount={segmentListDataCount}
                rowActionBar={getRowActionBar}
                columns={getTableColumns()}
                isLoading={isSegmentListLoading}
                isShowRefreshButton={true}
                isShowColumnChooser={isShowColumnChooser}
                isShowVerticalDots={isShowVerticalDots}
                setPagingPageSize={changePageSize}
                setPagingPageNumber={changePageNumber}
                sorting={dragDisable ? baseRequestBody.Sorting : {}}
                setSorting={changeSorting}
                onRefreshClick={getTableRefreshFn}
                headerActions={isNil(headerConfig) ? getHeaderActions() : undefined}
                headerConfig={headerConfig}
                isHasLeftHeaderActions={isHasLeftHeaderActions}
                headerLeftActions={headerLeftActions}
                isHasRowMultiSelect={isHasRowMultiSelect}
                isHasBulkActions={isHasBulkActions}
                archiveSelectedRows={archiveSelectedRows}
                unarchiveSelectedRows={unarchiveSelectedRows}
                deleteSelectedRows={deleteSelectedRows}
                isHasRowRadioSelect={isHasRowRadioSelect}
                getRadioSelectedRow={getRadioSelectedRow}
                defaultRadioSelectedRow={defaultRadioSelectedRow}
                getDragAcceptType={getDragAcceptType}
                CustomPreview={CustomPreview}
                receivedCurrentPageNumber={baseRequestBody.Pageing.PageNumber}
                withQueryParams={withQueryParams}
                radioSelectColumnConfig={radioSelectColumnConfig}
            />

            <SegmentActionsModals
                isDeleteModalOpenedState={isDeleteModalOpenedState}
                isConvertToReportModalOpenedState={isConvertToReportModalOpenedState}
                isArchiveModalOpenedState={isArchiveModalOpenedState}
                isUnArchiveModalOpenedState={isUnArchiveModalOpenedState}
                isInfoModalOpenedState={isInfoModalOpenedState}
                isUsedInConfirmationModalOpenedState={isUsedInConfirmationModalOpenedState}
                onCloseModalHandler={onCloseModalHandler}
                modalsStateSetDict={modalsStateSetDict}
                openedModalData={openedModalData}
                goToEditSegment={goToEditSegment}
            />
        </>
    );
});

SegmentList.displayName = 'SegmentList';

export default memo(SegmentList);
